import { logEvent } from "./logger";
import { API_BASE_URL } from "./apiConfig";

const logEndpoint = `${API_BASE_URL}/events/frontend-log`;

const normalizeError = (error: unknown): { name: string; message: string; stack?: string } => {
    if (error instanceof Error) {
        return { name: error.name, message: error.message, stack: error.stack };
    }
    if (typeof error === "string") {
        return { name: "Error", message: error };
    }
    return { name: "Error", message: JSON.stringify(error) };
};

export const logFrontendError = async (
    component: string,
    message: string,
    error: unknown,
    details?: Record<string, unknown>
): Promise<void> => {
    const normalized = normalizeError(error);
    const payload = {
        level: "ERROR" as const,
        component,
        message,
        details: {
            ...details,
            error: normalized
        }
    };

    logEvent(payload);

    const body = {
        ...payload,
        path: typeof window !== "undefined" ? window.location.pathname : undefined,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined
    };

    if (typeof fetch !== "function") {
        return;
    }

    try {
        await fetch(logEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        });
    } catch (networkError) {
        console.error("Failed to forward frontend error log", networkError);
    }
};

/** The message the API actually sent, or null if it did not send one.
 *
 * WHY THIS IS SHARED RATHER THAN LOCAL
 *
 * It lived as a closure inside `ManagementPlaceholders.tsx`, so the one page
 * that had it surfaced real errors and everybody else fell back to a fixed
 * string. `ManageStudentsPage` told an admin "Double-check the details" when
 * the server had said "a user with this email already exists" -- advice that
 * cannot be acted on, in place of the answer.
 *
 * Handles the shapes RTK Query actually produces: `error.data.detail` for
 * FastAPI's HTTPException, `error.data.message` for anything hand-rolled, and
 * `error.error` for a transport failure that never reached the API.
 */
export const extractErrorMessage = (error: unknown): string | null => {
    if (!error) {
        return null;
    }
    if (typeof error === "string") {
        return error;
    }
    if (typeof error === "object") {
        const candidate = error as {
            data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }>; message?: string };
            error?: string;
        };
        if (candidate.data) {
            if (typeof candidate.data.detail === "string") {
                return candidate.data.detail;
            }
            /* FastAPI's 422 sends `detail` as a LIST of {loc, msg}. It used to
               fall through to null, so every form showed a generic "Unable to
               ..." and never said which field was wrong (coaching audit, menu 2). */
            if (Array.isArray(candidate.data.detail) && candidate.data.detail.length) {
                return candidate.data.detail
                    .map((item) => {
                        const field = String((item.loc ?? []).slice(-1)[0] ?? "").replace(/_/g, " ");
                        const msg = String(item.msg ?? "").replace(/^Value error, /, "");
                        return field && field !== "body" ? `${field}: ${msg}` : msg;
                    })
                    .join("; ");
            }
            if (typeof candidate.data.message === "string") {
                return candidate.data.message;
            }
        }
        if (typeof candidate.error === "string") {
            return candidate.error;
        }
    }
    return null;
};
